import React, { useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, ShieldCheck, Trash2, Pencil, KeyRound, Copy, Check, AlertTriangle, QrCode, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const ROLES = ["admin", "directeur", "chef", "superviseur", "agent", "auditeur"];
const ROLE_BADGES = {
  admin: "bg-red-100 text-red-700",
  directeur: "bg-violet-100 text-violet-700",
  chef: "bg-blue-100 text-blue-700",
  superviseur: "bg-indigo-100 text-indigo-700",
  agent: "bg-emerald-100 text-emerald-700",
  auditeur: "bg-slate-200 text-slate-700",
};

const EMPTY_FORM = { name: "", role: "agent", fonction: "", service: "", telephone: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  // Access-code display modal
  const [codeModal, setCodeModal] = useState(null); // { user, code }
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const buildLoginUrl = (code) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    return `${origin}/login?code=${encodeURIComponent(code)}`;
  };

  const downloadQr = (userName, userId) => {
    try {
      const canvas = document.getElementById(`qr-canvas-${userId || "new"}`);
      if (!canvas) return;
      const url = canvas.toDataURL("image/png");
      const link = document.createElement("a");
      link.href = url;
      const safeName = (userName || "utilisateur").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase();
      link.download = `dkconnect-qr-${safeName}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("QR code téléchargé");
    } catch (err) {
      toast.error("Téléchargement impossible");
      console.warn("[Users] QR download failed", err);
    }
  };

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      role: u.role || "agent",
      fonction: u.fonction || "",
      service: u.service || "",
      telephone: u.telephone || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Le nom complet est obligatoire");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, form);
        toast.success("Utilisateur mis à jour");
        setOpen(false);
        load();
      } else {
        const { data } = await api.post("/users", form);
        setOpen(false);
        // Show generated code in modal (visible ONCE)
        setCodeModal({ user: data.user, code: data.access_code });
        setCopied(false);
        setShowQr(false);
        load();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer définitivement ${name} ?`)) return;
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utilisateur supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const regenerateCode = async (u) => {
    if (!window.confirm(`Régénérer le code d'accès de ${u.name} ?\nL'ancien code sera immédiatement invalidé.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/regenerate-code`, {});
      setCodeModal({ user: u, code: data.access_code });
      setCopied(false);
      setShowQr(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const copyCode = async () => {
    if (!codeModal?.code) return;
    try {
      await navigator.clipboard.writeText(codeModal.code);
      setCopied(true);
      toast.success("Code copié dans le presse-papier");
    } catch {
      toast.error("Copie impossible — sélectionnez et copiez manuellement");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Administration"
        title="Utilisateurs"
        description="Créez, éditez et gérez les codes d'accès de votre équipe."
        testid="users-header"
        actions={
          <Button onClick={openCreate} className="bg-blue-900 hover:bg-blue-800 rounded-full" data-testid="user-add-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter un utilisateur
          </Button>
        }
      />

      <Card className="border border-slate-200 bg-white dark:bg-slate-900 dark:border-slate-800">
        <CardContent className="p-4">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom complet</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Fonction</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Téléphone</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-400"><ShieldCheck className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun utilisateur</TableCell></TableRow>
                ) : users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium text-slate-900 dark:text-slate-100">{u.name}</TableCell>
                    <TableCell>
                      <Badge className={`${ROLE_BADGES[u.role] || "bg-slate-100"} border-transparent`}>{u.role}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300 text-sm">{u.fonction || "—"}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300 text-sm">{u.service || "—"}</TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300 text-sm font-mono">{u.telephone || "—"}</TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="sm" onClick={() => startEdit(u)} data-testid={`user-edit-${u.id}`} title="Modifier">
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => regenerateCode(u)} data-testid={`user-regen-code-${u.id}`} title="Régénérer le code d'accès">
                        <KeyRound className="w-4 h-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(u.id, u.name)} data-testid={`user-delete-${u.id}`} title="Supprimer">
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? "Modifier l'utilisateur" : "Ajouter un utilisateur"}</DialogTitle>
            <DialogDescription>
              {editing ? "Modifiez les informations. Pour changer le code d'accès, utilisez le bouton clé." : "Un code d'accès unique sera généré automatiquement et affiché une seule fois."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nom complet *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Ex : Marie Diop" data-testid="user-form-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Rôle *</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })} placeholder="+221..." data-testid="user-form-telephone" />
              </div>
            </div>
            <div>
              <Label>Fonction</Label>
              <Input value={form.fonction} onChange={(e) => setForm({ ...form, fonction: e.target.value })} placeholder="Ex : Chef de service Réclamations" data-testid="user-form-fonction" />
            </div>
            <div>
              <Label>Service / Département</Label>
              <Input value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} placeholder="Ex : Support client" data-testid="user-form-service" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="bg-blue-900 hover:bg-blue-800" data-testid="user-form-submit">
              {saving ? "Enregistrement…" : (editing ? "Enregistrer" : "Créer & générer le code")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Access code reveal dialog — shown only once at creation/regeneration */}
      <Dialog open={!!codeModal} onOpenChange={(v) => { if (!v) setCodeModal(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-emerald-600" />
              Code d&apos;accès pour {codeModal?.user?.name}
            </DialogTitle>
            <DialogDescription>
              Notez ou copiez ce code maintenant — il ne sera plus jamais affiché en clair.
            </DialogDescription>
          </DialogHeader>

          <div className="my-4">
            <div className="bg-slate-900 dark:bg-slate-950 rounded-2xl p-6 text-center">
              <div className="text-[10px] uppercase tracking-[0.28em] text-slate-400 mb-2">Code d&apos;accès</div>
              <div className="font-mono text-3xl font-bold text-emerald-400 tracking-widest select-all" data-testid="new-access-code">
                {codeModal?.code}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-3">
              <Button
                onClick={copyCode}
                variant="outline"
                className="rounded-full"
                data-testid="copy-access-code-btn"
              >
                {copied ? <><Check className="w-4 h-4 mr-2 text-emerald-600" /> Copié !</> : <><Copy className="w-4 h-4 mr-2" /> Copier</>}
              </Button>
              <Button
                onClick={() => setShowQr((v) => !v)}
                variant="outline"
                className="rounded-full"
                data-testid="show-qr-btn"
              >
                <QrCode className="w-4 h-4 mr-2" /> {showQr ? "Masquer QR" : "Afficher QR"}
              </Button>
            </div>

            {showQr && codeModal?.code && (
              <div className="mt-4 flex flex-col items-center gap-3 p-4 bg-white rounded-2xl border border-slate-200">
                <QRCodeCanvas
                  id={`qr-canvas-${codeModal.user?.id || "new"}`}
                  value={buildLoginUrl(codeModal.code)}
                  size={220}
                  level="M"
                  includeMargin={true}
                  data-testid="access-code-qr"
                />
                <p className="text-xs text-slate-500 text-center max-w-xs">
                  Scannez ce QR code depuis un mobile pour vous connecter en 1 tap. Le lien contient le code d&apos;accès — traitez-le comme confidentiel.
                </p>
                <Button
                  onClick={() => downloadQr(codeModal.user?.name || "user", codeModal.user?.id)}
                  variant="outline"
                  className="rounded-full"
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4 mr-2" /> Télécharger le QR
                </Button>
              </div>
            )}
          </div>

          <div className="text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-lg p-3 flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-amber-800 dark:text-amber-200">
              <b>Confidentiel</b> — transmettez ce code à <b>{codeModal?.user?.name}</b> via un canal sécurisé.
              Une fois fermée cette fenêtre, seul l&apos;admin pourra régénérer un nouveau code.
            </div>
          </div>

          <DialogFooter>
            <Button onClick={() => setCodeModal(null)} className="bg-blue-900 hover:bg-blue-800" data-testid="close-access-code-btn">
              J&apos;ai noté le code
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
